import { create } from 'zustand';
import { AIService } from 'src/services/ai';
import { applicationService } from 'src/services/application';
import {
  EstimationRequest,
  EstimationResponse,
} from 'src/services/interfaces/ai';
import { Application } from 'src/services/interfaces/application';
import { handleError, ErrorType } from '@utils/errorHandling';

import { useApplicationStore } from './application';
import { useInsuranceStore } from './insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useUnderReviewStore } from './under-review';

interface FinalSubmissionState {
  // Loading states
  isUnderwritingLoading: boolean;
  isSubmittingApplication: boolean;

  // AI underwriting result
  underwritingResult: EstimationResponse | null;
  underwritingError: string | null;

  // Application submission
  submissionError: string | null;
  isSubmitted: boolean;

  // Actions
  runUnderwriting: (data: EstimationRequest | Application) => Promise<void>;
  submitFinalApplication: (
    data: Application,
    token?: string,
    signature?: string
  ) => Promise<void>;
  resetState: () => void;
  clearErrors: () => void;
  getEstimation: (id: number | string) => Promise<void>;
}

export const useFinalSubmissionStore = create<FinalSubmissionState>(
  (set, get) => ({
    isUnderwritingLoading: false,
    isSubmittingApplication: false,
    underwritingResult: null,
    underwritingError: null,
    submissionError: null,
    isSubmitted: false,

    getEstimation: async (id: number | string) => {
      set({ isUnderwritingLoading: true });
      try {
        const result = await applicationService.getEstimation(id);
        console.log('Estimation result:', result);

        // Sync stores from API (sets phqRejectedAt, acceptedTier, isApplicationAccepted, etc.)
        await useApplicationStore.getState().updateTierFromApplicationState();

        if (result.status === 'rejected') {
          const rejectionReason = result.details?.reason;
          const isApiErrorRejection = rejectionReason === 'API Error';
          const currentState = useApplicationStore.getState();
          const currentApplication = currentState.applications[0];

          if (!isApiErrorRejection) {
            useApplicationStore.getState().setIsMainPlanRejected(true);
            useOnboardingStore.getState().rejectMedicalPlan();
          }

          if (
            !isApiErrorRejection &&
            currentApplication &&
            currentApplication.plans.length > 0
          ) {
            const fullPlans = useInsuranceStore.getState().plans;

            console.log('Full plans:', fullPlans);

            const rejectedPlanId = result?.priceId;
            console.log('Rejected plan ID:', rejectedPlanId);

            try {
              const data = useApplicationStore.getState().applications[0];
              const fullPlans = useInsuranceStore.getState().plans;
              const newPlans = [];
              if (fullPlans.length > 0) {
                const dataPlans = [...data.plans];
                for (const plan of dataPlans) {
                  const myMajorPlan = fullPlans
                    .find(
                      (p) => p.category === 'Major Medical' && p.id === plan.id
                    )
                    ?.prices.find((item) => item.tier === plan.tier);
                  if (!myMajorPlan) {
                    newPlans.push(plan);
                  }
                }
                data.plans = dataPlans;
              }
              data.plans = newPlans;
            } catch (error) {
              console.log('Error:', error);
            }

            // if (rejectedPlanId) {
            //   const rejectedPlan = fullPlans.find(
            //     (plan) => plan.id === rejectedPlanId
            //   );

            //   if (rejectedPlan) {
            //     const updatedPlans = currentApplication.plans.filter(
            //       (plan) => plan.id !== rejectedPlan.id
            //     );
            //     console.log(1)
            //     await useApplicationStore.getState().updateApplication({
            //       ...currentApplication,
            //       plans: updatedPlans,
            //     });
            //   }
            // }
          }
        }

        if (result.status === 'human_review') {
          useUnderReviewStore.getState().setSessionLockedForHumanReview();
        }

        set({
          underwritingResult: result,
          isUnderwritingLoading: false,
        });
      } catch (error: any) {
        handleError(ErrorType.UNDERWRITING, error);

        await useApplicationStore.getState().updateTierFromApplicationState();
        const { phqRejectedAt } = useApplicationStore.getState();
        if (phqRejectedAt) {
          useApplicationStore.getState().setIsMainPlanRejected(true);
          useOnboardingStore.getState().rejectMedicalPlan();
          set({
            underwritingResult: {
              status: 'rejected',
              details: { reason: 'Human Review Rejected' },
            } as EstimationResponse,
            underwritingError: null,
            isUnderwritingLoading: false,
          });
          return;
        }

        const rejectResult: EstimationResponse = {
          status: 'rejected',
          details: {
            reason: 'API Error',
            error:
              error?.response?.data?.message ||
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              'Failed to get estimation',
          },
        };

        set({
          underwritingResult: rejectResult,
          underwritingError: 'Failed to get estimation',
          isUnderwritingLoading: false,
        });
      }
    },

    runUnderwriting: async (data: EstimationRequest | Application) => {
      set({ isUnderwritingLoading: true, underwritingError: null });

      try {
        const result = await AIService.runEstimation(data);
        if (result.status === 'human_review') {
          useUnderReviewStore.getState().setSessionLockedForHumanReview();
        }
        set({
          underwritingResult: result,
          isUnderwritingLoading: false,
        });
      } catch (error: unknown) {
        const anyError: any = error as any;

        // If application was already rejected (e.g. by admin), fetch state and show reject UI instead of API error
        await useApplicationStore.getState().updateTierFromApplicationState();
        const { phqRejectedAt } = useApplicationStore.getState();
        if (phqRejectedAt) {
          useApplicationStore.getState().setIsMainPlanRejected(true);
          useOnboardingStore.getState().rejectMedicalPlan();
          handleError(ErrorType.UNDERWRITING, error);
          set({
            underwritingResult: {
              status: 'rejected',
              details: { reason: 'Human Review Rejected' },
            } as EstimationResponse,
            underwritingError: null,
            isUnderwritingLoading: false,
          });
          return;
        }

        const statusCode = anyError?.response?.status;
        if (statusCode === 400 || statusCode === 422) {
          const apiDetail = anyError?.response?.data?.detail;
          const firstValidation =
            Array.isArray(apiDetail) && apiDetail[0]?.msg
              ? apiDetail[0].msg
              : undefined;
          const validationMessage =
            firstValidation ||
            (typeof apiDetail === 'string' && apiDetail) ||
            'Incorrect data for calculation';

          handleError(ErrorType.UNDERWRITING, error);
          set({
            underwritingResult: null,
            underwritingError: validationMessage,
            isUnderwritingLoading: false,
          });
          return;
        }

        handleError(ErrorType.UNDERWRITING, error);
        const combinedMessage =
          anyError?.response?.data?.message ||
          (error instanceof Error
            ? error.message
            : 'Unexpected error occurred');

        const rejectResult: EstimationResponse = {
          status: 'rejected',
          details: {
            reason: 'API Error',
            error: combinedMessage,
          },
        } as any;

        set({
          underwritingResult: rejectResult,
          underwritingError: combinedMessage,
          isUnderwritingLoading: false,
        });
      }
    },

    submitFinalApplication: async (
      data: Application,
      token?: string,
      signature?: string
    ) => {
      set({ isSubmittingApplication: true, submissionError: null });

      try {
        const currentApp = useApplicationStore.getState().applications[0];
        const applicationId =
          useApplicationStore.getState().currentApplicationId;

        if (!currentApp || !applicationId) {
          const error = new Error('Application or application ID is missing');
          handleError(ErrorType.APPLICATION_SUBMIT, error);
          set({
            submissionError: 'Application or application ID is missing',
            isSubmittingApplication: false,
          });
          throw error;
        }

        if (token) {
          await applicationService.submitApplication(applicationId, {
            paymentMethodId: token,
            signature,
          });
        } else {
          await applicationService.submitApplication(applicationId, {
            paymentMethodId: '',
            signature,
          });
        }

        set({
          isSubmitted: true,
          isSubmittingApplication: false,
        });
      } catch (error: unknown) {
        console.error(
          '[final-submission] Error in submitFinalApplication:',
          error
        );

        // Extract error message from axios response if available
        let errorMessage = 'Unexpected error occurred. Please try later.';
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as any;
          const responseData = axiosError.response?.data;

          // Try to extract error message from different response formats
          if (responseData) {
            if (
              Array.isArray(responseData.errors) &&
              responseData.errors.length > 0
            ) {
              errorMessage = responseData.errors[0];
            } else if (responseData.message) {
              errorMessage = responseData.message;
            } else if (responseData.error) {
              errorMessage = responseData.error;
            } else if (typeof responseData === 'string') {
              errorMessage = responseData;
            }
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        handleError(ErrorType.APPLICATION_SUBMIT, error);
        set({
          submissionError: errorMessage,
          isSubmittingApplication: false,
          isSubmitted: false, // Make sure isSubmitted is false on error
        });

        // Re-throw error so calling code can prevent navigation
        throw error;
      }
    },

    resetState: () => {
      set({
        isUnderwritingLoading: false,
        isSubmittingApplication: false,
        underwritingResult: null,
        underwritingError: null,
        submissionError: null,
        isSubmitted: false,
      });
    },

    clearErrors: () => {
      set({
        underwritingError: null,
        submissionError: null,
      });
    },
  })
);
